import React, { useState, useEffect } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Autocomplete, Typography, Box,
    CircularProgress
} from "@mui/material";
import ApiRequestService from "../component/ApiRequestService";
import { NotificationManager } from "react-notifications";

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {Object} props.item - tutorial row (quiz_id, quiz_name, subject_id, ...) or explanation row (explanation_id, explanation_name, subject_id, ...)
 * @param {"tutorial"|"explanation"} props.type
 * @param {Function} props.onClose
 * @param {Function} props.onSaved
 */
const ChangeTutorialLocationDialog = ({ open, item, type = "tutorial", onClose, onSaved }) => {
    const [subjects, setSubjects] = useState([]);
    const [topics, setTopics] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);

    const [selectedSubject, setSelectedSubject] = useState(null);
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedSubcategory, setSelectedSubcategory] = useState(null);

    const [saving, setSaving] = useState(false);
    const [prefilling, setPrefilling] = useState(false);

    const isTutorial = type === "tutorial";
    const itemName = isTutorial ? item?.quiz_name : item?.explanation_name;

    useEffect(() => {
        if (open && item) {
            setSelectedSubject(null);
            setSelectedTopic(null);
            setSelectedCategory(null);
            setSelectedSubcategory(null);
            setTopics([]);
            setCategories([]);
            setSubcategories([]);
            prefillSelections();
        }
    }, [open]);

    const prefillSelections = async () => {
        if (!item) return;
        setPrefilling(true);
        try {
            // 1. Fetch subjects and prefill
            const subRes = await ApiRequestService({ method: "post", url: "/getAllSubjects" });
            const subjectList = subRes?.data?.data?.subject_names || [];
            setSubjects(subjectList);

            const matchedSubject = subjectList.find(s => s.subject_id === item.subject_id);
            const subjectOption = matchedSubject
                ? { id: matchedSubject.subject_id, label: matchedSubject.subject_name, data: matchedSubject }
                : null;
            setSelectedSubject(subjectOption);

            if (!matchedSubject) { setPrefilling(false); return; }

            // 2. Fetch topics and prefill
            const topRes = await ApiRequestService({ method: "post", url: "/getAllTopics", data: { subject_id: matchedSubject.subject_id } });
            const topicList = topRes?.data?.data?.topic_names || [];
            setTopics(topicList);

            const matchedTopic = topicList.find(t => t.topic_id === item.topic_id);
            const topicOption = matchedTopic
                ? { id: matchedTopic.topic_id, label: matchedTopic.topic_name, data: matchedTopic }
                : null;
            setSelectedTopic(topicOption);

            if (!matchedTopic) { setPrefilling(false); return; }

            // 3. Fetch categories and prefill
            const catRes = await ApiRequestService({ method: "post", url: "/getAllCategories", data: { subject_id: matchedSubject.subject_id, topic_id: matchedTopic.topic_id } });
            const categoryList = catRes?.data?.data?.category_names || [];
            setCategories(categoryList);

            const matchedCategory = categoryList.find(c => c.category_id === item.category_id);
            const categoryOption = matchedCategory
                ? { id: matchedCategory.category_id, label: matchedCategory.category_name, data: matchedCategory }
                : null;
            setSelectedCategory(categoryOption);

            if (!matchedCategory) { setPrefilling(false); return; }

            // 4. Fetch subcategories and prefill
            const subcatRes = await ApiRequestService({ method: "post", url: "/getAllSubCategories", data: { subject_id: matchedSubject.subject_id, topic_id: matchedTopic.topic_id, category_id: matchedCategory.category_id } });
            const subcategoryList = subcatRes?.data?.data?.subCategory_names || [];
            setSubcategories(subcategoryList);

            const matchedSubcategory = subcategoryList.find(sc => sc.subcategory_id === item.subcategory_id);
            const subcategoryOption = matchedSubcategory
                ? { id: matchedSubcategory.subcategory_id, label: matchedSubcategory.subcategory_name, data: matchedSubcategory }
                : null;
            setSelectedSubcategory(subcategoryOption);
        } catch (err) {
            // Prefill failed silently, user can still pick manually
        } finally {
            setPrefilling(false);
        }
    };

    const getTopics = (subjectId) => {
        ApiRequestService({
            method: "post",
            url: "/getAllTopics",
            data: { subject_id: subjectId },
        }).then((res) => {
            if (res && res.data && res.data.data) {
                setTopics(res.data.data.topic_names);
            }
        });
    };

    const getCategories = (subjectId, topicId) => {
        ApiRequestService({
            method: "post",
            url: "/getAllCategories",
            data: { subject_id: subjectId, topic_id: topicId },
        }).then((res) => {
            if (res && res.data && res.data.data) {
                setCategories(res.data.data.category_names);
            }
        });
    };

    const getSubcategories = (subjectId, topicId, categoryId) => {
        ApiRequestService({
            method: "post",
            url: "/getAllSubCategories",
            data: { subject_id: subjectId, topic_id: topicId, category_id: categoryId },
        }).then((res) => {
            if (res && res.data && res.data.data) {
                setSubcategories(res.data.data.subCategory_names);
            }
        });
    };

    const handleSubjectChange = (_, value) => {
        setSelectedSubject(value);
        setSelectedTopic(null);
        setSelectedCategory(null);
        setSelectedSubcategory(null);
        setTopics([]);
        setCategories([]);
        setSubcategories([]);
        if (value) getTopics(value.id);
    };

    const handleTopicChange = (_, value) => {
        setSelectedTopic(value);
        setSelectedCategory(null);
        setSelectedSubcategory(null);
        setCategories([]);
        setSubcategories([]);
        if (value && selectedSubject) getCategories(selectedSubject.id, value.id);
    };

    const handleCategoryChange = (_, value) => {
        setSelectedCategory(value);
        setSelectedSubcategory(null);
        setSubcategories([]);
        if (value && selectedSubject && selectedTopic)
            getSubcategories(selectedSubject.id, selectedTopic.id, value.id);
    };

    const handleSave = () => {
        if (!selectedSubject || !selectedTopic) {
            NotificationManager.warning("Please select at least a subject and topic");
            return;
        }

        const payload = {
            subject_id: selectedSubject.id,
            topic_id: selectedTopic.id,
            category_id: selectedCategory ? selectedCategory.id : 0,
            subcategory_id: selectedSubcategory ? selectedSubcategory.id : 0,
        };

        if (isTutorial) {
            payload.quiz_id = item.quiz_id;
        } else {
            payload.explanation_id = item.explanation_id;
            payload.explanation_name = item.explanation_name;
            payload.text = item.text;
            payload.old_subject_id = item.subject_id;
            payload.old_topic_id = item.topic_id;
            payload.old_category_id = item.category_id;
            payload.old_subcategory_id = item.subcategory_id;
        }

        setSaving(true);
        ApiRequestService({
            method: "post",
            url: isTutorial ? "/changeTutorialLocation" : "/changeExplanationLocation",
            data: payload,
        }).then((res) => {
            if (res && res.data) {
                if (res.data.message && res.data.message !== "") {
                    NotificationManager.error(res.data.message);
                    return;
                }
                NotificationManager.success(`${isTutorial ? "Tutorial" : "Explanation"} location updated successfully`);
                onSaved();
            }
        }).catch(() => {
            NotificationManager.error(`Failed to update ${isTutorial ? "tutorial" : "explanation"} location`);
        }).finally(() => setSaving(false));
    };

    return (
        <Dialog open={open} maxWidth="sm" fullWidth disableEscapeKeyDown>
            <DialogTitle>
                <Typography variant="h6" fontWeight={600}>
                    Change {isTutorial ? "Tutorial" : "Explanation"} Location
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                    {itemName}
                </Typography>
            </DialogTitle>

            <DialogContent>
                {prefilling ? (
                    <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                        <CircularProgress size={28} />
                    </Box>
                ) : (
                    <Box display="flex" flexDirection="column" gap={2} mt={1}>
                        <Autocomplete
                            size="small"
                            options={subjects ? subjects.map(s => ({
                                id: s.subject_id,
                                label: s.subject_name,
                                data: s,
                            })) : []}
                            value={selectedSubject}
                            onChange={handleSubjectChange}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            getOptionLabel={(option) => option.label}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Subject"
                                    placeholder="Select Subject"
                                    InputProps={{
                                        ...params.InputProps,
                                        sx: { borderRadius: "6px" },
                                    }}
                                />
                            )}
                        />

                        <Autocomplete
                            size="small"
                            options={topics ? topics.map(t => ({
                                id: t.topic_id,
                                label: t.topic_name,
                                data: t,
                            })) : []}
                            value={selectedTopic}
                            onChange={handleTopicChange}
                            disabled={!selectedSubject}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            getOptionLabel={(option) => option.label}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Topic"
                                    placeholder="Select Topic"
                                    InputProps={{
                                        ...params.InputProps,
                                        sx: { borderRadius: "6px" },
                                    }}
                                />
                            )}
                        />

                        <Autocomplete
                            size="small"
                            options={categories ? categories.map(c => ({
                                id: c.category_id,
                                label: c.category_name,
                                data: c,
                            })) : []}
                            value={selectedCategory}
                            onChange={handleCategoryChange}
                            disabled={!selectedTopic}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            getOptionLabel={(option) => option.label}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Category"
                                    placeholder="Select Category"
                                    InputProps={{
                                        ...params.InputProps,
                                        sx: { borderRadius: "6px" },
                                    }}
                                />
                            )}
                        />

                        <Autocomplete
                            size="small"
                            options={subcategories ? subcategories.map(sc => ({
                                id: sc.subcategory_id,
                                label: sc.subcategory_name,
                                data: sc,
                            })) : []}
                            value={selectedSubcategory}
                            onChange={(_, value) => setSelectedSubcategory(value)}
                            disabled={!selectedCategory}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            getOptionLabel={(option) => option.label}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Subcategory"
                                    placeholder="Select Subcategory"
                                    InputProps={{
                                        ...params.InputProps,
                                        sx: { borderRadius: "6px" },
                                    }}
                                />
                            )}
                        />
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} variant="outlined" disabled={saving}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={saving || prefilling || !selectedTopic}
                >
                    {saving ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ChangeTutorialLocationDialog;
